import type {
  FeedbackItem,
  FinalishBridgeOptions,
  FinalishChildMessage,
  FinalishParentMessage,
} from './types';
import {
  describeElement,
  feedbackBelongsToCurrentRoute,
  isElementRenderable,
  readAnchor,
  resolveElement,
  resolveFeedbackPoint,
  viewportSnapshot,
} from './anchors';
import {
  blurActivePageControl,
  elementDisplayName,
  reviewTargetFromEvent,
} from './dom';
import { placePin, viewportRect } from './geometry';

const PROTOCOL_VERSION = 1;

export class FinalishReviewBridge {
  private readonly allowedParentOrigins: Set<string>;
  private host?: HTMLDivElement;
  private shadow?: ShadowRoot;
  private parentOrigin?: string;
  private parentWindow?: Window;
  private mode: 'browse' | 'comment' = 'browse';
  private feedback: FeedbackItem[] = [];
  private mutationObserver?: MutationObserver;
  private routeHref = location.href;
  private destroyed = false;
  private highlightedElement?: Element;
  private highlightKind: 'hover' | 'selected' = 'hover';
  private highlightTimer?: number;
  private originalPushState?: History['pushState'];
  private originalReplaceState?: History['replaceState'];
  private patchedPushState?: History['pushState'];
  private patchedReplaceState?: History['replaceState'];

  constructor(options: FinalishBridgeOptions = {}) {
    this.allowedParentOrigins = new Set(
      (options.allowedParentOrigins ?? []).map(normalizeOrigin),
    );
  }

  mount(): void {
    if (this.host) return;
    this.destroyed = false;
    this.createSurface();
    window.addEventListener('message', this.handleMessage);
    window.addEventListener('scroll', this.handleViewportChange, true);
    window.addEventListener('resize', this.handleViewportChange);
    document.addEventListener('pointerdown', this.handlePointer, true);
    document.addEventListener('pointermove', this.handlePointerMove, true);
    document.addEventListener('pointerleave', this.handlePointerLeave, true);
    document.addEventListener('click', this.blockPageInteraction, true);
    document.addEventListener('auxclick', this.blockPageInteraction, true);
    document.addEventListener('beforeinput', this.blockPageInteraction, true);
    document.addEventListener('submit', this.blockPageInteraction, true);
    document.addEventListener('keydown', this.handleKeydown);
    this.patchHistory();
    this.mutationObserver = new MutationObserver(() => {
      this.detectRouteChange();
      this.renderPins();
      this.renderHighlight();
    });
    this.mutationObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
    });
    this.postToParent({
      source: 'finalish-sdk',
      type: 'ready',
      version: PROTOCOL_VERSION,
      href: location.href,
      title: document.title,
      viewport: viewportSnapshot(),
    });
  }

  destroy(): void {
    this.destroyed = true;
    window.removeEventListener('message', this.handleMessage);
    window.removeEventListener('scroll', this.handleViewportChange, true);
    window.removeEventListener('resize', this.handleViewportChange);
    document.removeEventListener('pointerdown', this.handlePointer, true);
    document.removeEventListener('pointermove', this.handlePointerMove, true);
    document.removeEventListener('pointerleave', this.handlePointerLeave, true);
    document.removeEventListener('click', this.blockPageInteraction, true);
    document.removeEventListener('auxclick', this.blockPageInteraction, true);
    document.removeEventListener('beforeinput', this.blockPageInteraction, true);
    document.removeEventListener('submit', this.blockPageInteraction, true);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('popstate', this.handleRouteSignal);
    window.removeEventListener('hashchange', this.handleRouteSignal);
    this.restoreHistory();
    this.mutationObserver?.disconnect();
    if (this.highlightTimer) window.clearTimeout(this.highlightTimer);
    document.documentElement.style.cursor = '';
    this.host?.remove();
    this.host = undefined;
    this.shadow = undefined;
  }

  private createSurface(): void {
    this.host = document.createElement('div');
    this.host.dataset.finalishOverlay = 'true';
    this.host.style.cssText =
      'position:fixed;inset:0;z-index:2147483000;pointer-events:none;';
    this.shadow = this.host.attachShadow({ mode: 'open' });
    this.shadow.innerHTML = `<style>${BRIDGE_STYLES}</style>
      <div class="highlight" hidden></div>
      <div class="highlight-label" hidden></div>
      <div class="pins" aria-live="polite"></div>
      <div class="status" hidden></div>`;
    document.body.append(this.host);
  }

  private readonly handleMessage = (event: MessageEvent): void => {
    if (!isParentMessage(event.data) || !this.isAllowedOrigin(event.origin)) {
      return;
    }
    if (event.source !== window.parent) return;
    this.parentOrigin = event.origin;
    this.parentWindow = event.source as Window;

    if (event.data.type === 'init') {
      this.feedback = event.data.feedback;
      this.setMode(event.data.mode);
      this.renderPins();
      this.post({
        source: 'finalish-sdk',
        type: 'ready',
        version: PROTOCOL_VERSION,
        href: location.href,
        title: document.title,
        viewport: viewportSnapshot(),
      });
    } else if (event.data.type === 'set-mode') {
      this.setMode(event.data.mode);
    } else if (event.data.type === 'set-feedback') {
      this.feedback = event.data.feedback;
      this.renderPins();
    } else if (event.data.type === 'focus-feedback') {
      this.focusFeedback(event.data.feedbackId);
    }
  };

  private isAllowedOrigin(origin: string): boolean {
    if (!origin || origin === 'null') return false;
    if (this.allowedParentOrigins.size > 0) {
      return this.allowedParentOrigins.has(normalizeOrigin(origin));
    }
    return document.referrer
      ? normalizeOrigin(document.referrer) === normalizeOrigin(origin)
      : false;
  }

  private setMode(mode: 'browse' | 'comment'): void {
    this.mode = mode;
    document.documentElement.style.cursor =
      mode === 'comment' ? 'crosshair' : '';
    if (mode === 'comment') blurActivePageControl();
    else this.clearHighlight();
    this.setStatus(
      mode === 'comment'
        ? 'Режим комментария: выберите элемент'
        : '',
    );
  }

  private readonly handlePointer = (event: PointerEvent): void => {
    if (
      this.mode !== 'comment' ||
      event.button !== 0 ||
      event.composedPath().includes(this.host as EventTarget)
    ) {
      return;
    }
    const target = reviewTargetFromEvent(event, this.host);
    if (!target) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const anchor = describeElement(target, event);
    this.highlight(target, 'selected');
    this.post({
      source: 'finalish-sdk',
      type: 'selection',
      href: location.href,
      title: document.title,
      anchor,
      position: { x: event.pageX, y: event.pageY },
      viewport: viewportSnapshot(),
    });
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (this.mode !== 'comment') return;
    const target = reviewTargetFromEvent(event, this.host);
    if (!target) {
      if (this.highlightKind === 'hover') this.clearHighlight();
      return;
    }
    if (
      target === this.highlightedElement &&
      this.highlightKind === 'hover'
    ) {
      return;
    }
    this.highlight(target, 'hover');
  };

  private readonly handlePointerLeave = (): void => {
    if (this.mode === 'comment' && this.highlightKind === 'hover') {
      this.clearHighlight();
    }
  };

  private readonly blockPageInteraction = (event: Event): void => {
    if (
      this.mode !== 'comment' ||
      event.composedPath().includes(this.host as EventTarget)
    ) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  private readonly renderPins = (): void => {
    const container = this.shadow?.querySelector<HTMLDivElement>('.pins');
    if (!container || this.destroyed) return;
    const viewport = viewportRect();
    const pins: HTMLButtonElement[] = [];
    this.feedback.filter(feedbackBelongsToCurrentRoute).forEach((item) => {
      const resolved = resolveFeedbackPoint(item);
      if (!resolved.found) return;
      const position = placePin(resolved, 40, viewport);
      if (!position.visible) return;
      const markerNumber = pins.length + 1;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `pin${item.status === 'resolved' ? ' is-resolved' : ''}`;
      button.style.left = `${position.left}px`;
      button.style.top = `${position.top}px`;
      button.setAttribute(
        'aria-label',
        `Комментарий ${markerNumber}: ${item.message}`,
      );
      button.innerHTML = `<span>${item.status === 'resolved' ? '✓' : markerNumber}</span>`;
      button.addEventListener('click', () => {
        this.post({
          source: 'finalish-sdk',
          type: 'feedback-selected',
          feedbackId: item.id,
        });
      });
      pins.push(button);
    });
    container.replaceChildren(...pins);
  };

  private readonly handleViewportChange = (): void => {
    this.renderPins();
    this.renderHighlight();
  };

  private focusFeedback(feedbackId: string): void {
    const item = this.feedback.find((entry) => entry.id === feedbackId);
    if (!item || !feedbackBelongsToCurrentRoute(item)) return;
    const anchor = readAnchor(item.selector);
    const element = anchor ? resolveElement(anchor) : null;
    if (!element || !isElementRenderable(element)) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.highlight(element, 'selected', 1800);
  }

  private highlight(
    element: Element,
    kind: 'hover' | 'selected',
    hideAfterMs?: number,
  ): void {
    if (this.highlightTimer) window.clearTimeout(this.highlightTimer);
    this.highlightedElement = element;
    this.highlightKind = kind;
    this.renderHighlight();
    if (hideAfterMs) {
      this.highlightTimer = window.setTimeout(() => {
        this.clearHighlight();
      }, hideAfterMs);
    }
  }

  private renderHighlight(): void {
    const highlight = this.shadow?.querySelector<HTMLDivElement>('.highlight');
    const label =
      this.shadow?.querySelector<HTMLDivElement>('.highlight-label');
    const element = this.highlightedElement;
    if (!highlight || !label || !element?.isConnected) {
      this.clearHighlight();
      return;
    }
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      this.clearHighlight();
      return;
    }
    highlight.hidden = false;
    label.hidden = false;
    highlight.classList.toggle(
      'is-selected',
      this.highlightKind === 'selected',
    );
    highlight.style.left = `${rect.left}px`;
    highlight.style.top = `${rect.top}px`;
    highlight.style.width = `${rect.width}px`;
    highlight.style.height = `${rect.height}px`;
    label.textContent = elementDisplayName(element);
    label.style.left = `${clamp(
      rect.left,
      8,
      Math.max(8, window.innerWidth - 220),
    )}px`;
    label.style.top = `${rect.top >= 36 ? rect.top - 30 : rect.bottom + 6}px`;
  }

  private clearHighlight(): void {
    if (this.highlightTimer) window.clearTimeout(this.highlightTimer);
    this.highlightTimer = undefined;
    this.highlightedElement = undefined;
    const highlight = this.shadow?.querySelector<HTMLDivElement>('.highlight');
    const label =
      this.shadow?.querySelector<HTMLDivElement>('.highlight-label');
    if (highlight) highlight.hidden = true;
    if (label) label.hidden = true;
  }

  private setStatus(message: string): void {
    const status = this.shadow?.querySelector<HTMLDivElement>('.status');
    if (!status) return;
    status.hidden = !message;
    status.textContent = message;
  }

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.mode === 'comment') {
      this.setMode('browse');
      this.post({
        source: 'finalish-sdk',
        type: 'mode-changed',
        mode: 'browse',
      });
    }
  };

  private patchHistory(): void {
    if (this.patchedPushState || this.patchedReplaceState) return;
    this.originalPushState = history.pushState;
    this.originalReplaceState = history.replaceState;
    this.patchedPushState = ((data, unused, url) => {
      const result = this.originalPushState?.call(history, data, unused, url);
      this.handleRouteSignal();
      return result;
    }) as History['pushState'];
    this.patchedReplaceState = ((data, unused, url) => {
      const result = this.originalReplaceState?.call(history, data, unused, url);
      this.handleRouteSignal();
      return result;
    }) as History['replaceState'];
    history.pushState = this.patchedPushState;
    history.replaceState = this.patchedReplaceState;
    window.addEventListener('popstate', this.handleRouteSignal);
    window.addEventListener('hashchange', this.handleRouteSignal);
  }

  private restoreHistory(): void {
    if (this.originalPushState && history.pushState === this.patchedPushState) {
      history.pushState = this.originalPushState;
    }
    if (this.originalReplaceState && history.replaceState === this.patchedReplaceState) {
      history.replaceState = this.originalReplaceState;
    }
    this.patchedPushState = undefined;
    this.patchedReplaceState = undefined;
  }

  private readonly handleRouteSignal = (): void => this.detectRouteChange();

  private detectRouteChange(): void {
    if (this.routeHref === location.href) return;
    this.routeHref = location.href;
    this.feedback = [];
    this.renderPins();
    this.post({
      source: 'finalish-sdk',
      type: 'route-changed',
      href: location.href,
      title: document.title,
    });
  }

  private post(message: FinalishChildMessage): void {
    this.parentWindow?.postMessage(message, this.parentOrigin ?? '*');
  }

  private postToParent(message: FinalishChildMessage): void {
    if (window.parent === window) return;
    const target = this.allowedParentOrigins.values().next().value ?? '*';
    window.parent.postMessage(message, target);
  }
}

function normalizeOrigin(value: string): string {
  return new URL(value).origin;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

function isParentMessage(value: unknown): value is FinalishParentMessage {
  return (
    typeof value === 'object' &&
    value !== null &&
    'source' in value &&
    value.source === 'finalish-review' &&
    'type' in value
  );
}

const BRIDGE_STYLES = `
  :host{--finalish-primary:#5417ee;--finalish-danger:#c83d55;
    font-family:"Onest",ui-sans-serif,system-ui,sans-serif}
  *{box-sizing:border-box}.pins{position:fixed;inset:0;pointer-events:none}
  .pin{position:absolute;width:40px;height:40px;padding:7px;border:0;background:transparent;
    pointer-events:auto;cursor:pointer}.pin span{display:grid;place-items:center;width:26px;height:26px;
    border:2px solid var(--finalish-primary);border-radius:999px;background:#fff;color:var(--finalish-primary);
    font:600 11px/1 "Onest",ui-sans-serif,system-ui,sans-serif;box-shadow:0 2px 7px rgb(33 26 55 / 20%)}
  .pin.is-resolved span{border-color:#898297;background:#898297;color:#fff}
  .pin.is-lost span{border-style:dashed}.pin:focus-visible{outline:2px solid var(--finalish-primary);outline-offset:1px}
  .highlight{position:fixed;border:2px solid var(--finalish-primary);background:rgb(84 23 238 / 8%);
    border-radius:3px;pointer-events:none;box-shadow:0 0 0 1px rgb(255 255 255 / 92%);
    transition:left 80ms ease,top 80ms ease,width 80ms ease,height 80ms ease}
  .highlight.is-selected{background:rgb(84 23 238 / 13%);box-shadow:0 0 0 1px #fff,0 0 0 3px rgb(84 23 238 / 22%)}
  .highlight-label{position:fixed;z-index:1;max-width:min(360px,calc(100vw - 16px));height:24px;
    overflow:hidden;padding:3px 7px;border-radius:4px;background:#211a37;color:#fff;
    font:600 12px/18px "Onest",ui-sans-serif,system-ui,sans-serif;text-overflow:ellipsis;
    white-space:nowrap;pointer-events:none}.status{position:fixed;left:50%;top:16px;translate:-50% 0;
    padding:8px 12px;border:1px solid #e2e1ea;border-radius:8px;background:#fff;color:#352e49;
    box-shadow:0 4px 14px rgb(33 26 55 / 12%);font:500 13px/18px "Onest",ui-sans-serif,system-ui,sans-serif}
  @media(prefers-reduced-motion:reduce){*{scroll-behavior:auto!important}}
`;
